/* ==========================================================================
   Desktop Sticky Cart — gift-unlock ("Unlock gifts at special prices") module.

   Fetches the current cart (/cart.js), posts it to the SAME IPA endpoint the
   existing cart uses, and renders the sample/gift tiers into the carousel track.
   Locked tiers show their unlock copy + progress; unlocked tiers expose a Claim
   button that adds the gift to the cart and refreshes the drawer.

   Parallel build: own code, same endpoint. Exposes window.StickyCartGifts.apply().
   ========================================================================== */
(function () {
  'use strict';

  var IPA_URL = 'https://supertails-backend.el.r.appspot.com/app/IPA-web/v2';

  // Guards the removeTheseFromCart auto-removal so it acts once per distinct cart
  // state and can't loop (mirrors the surprise-gift / coupon-reconcile pattern).
  var lastRemovedSig = null;

  function cartSig(cart) {
    return ((cart && cart.items) || [])
      .map(function (i) { return i.variant_id + ':' + i.quantity; })
      .sort()
      .join('|');
  }

  // Remove a variant from the cart (parity with cart-revamp removeFreeGiftItemNew,
  // but no full-page reload — the drawer re-renders via the refresh callback).
  function removeVariant(variantId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(variantId), quantity: 0 })
    }).then(function (r) { return r.json(); }).catch(function () {});
  }

  function getLocation() {
    try {
      return JSON.parse(localStorage.getItem('userLocation')) || {};
    } catch (e) {
      return {};
    }
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function apply(scope, opts) {
    scope = scope || document;
    opts = opts || {};
    var container = scope.querySelector('[data-st-gift-unlock]');
    if (!container) return;
    var track = container.querySelector('[data-st-gift-track]');
    var skeleton = container.querySelector('[data-st-gift-skeleton]');
    var currentCart = null;

    function showSkeleton() {
      container.hidden = false;
      if (skeleton) skeleton.hidden = false;
      if (track) track.hidden = true;
    }
    function hideSkeleton() {
      if (skeleton) skeleton.hidden = true;
      if (track) track.hidden = false;
    }

    // Loader state while the IPA request is in flight.
    showSkeleton();

    getCart()
      .then(function (cart) {
        currentCart = cart;
        var loc = getLocation();
        // Gift/IPA unlock thresholds are AOV-based — compute them on the in-stock cart
        // so OOS items don't inflate spend toward an unlock.
        var items = (window.StickyCart && typeof window.StickyCart.filterInStock === 'function')
          ? window.StickyCart.filterInStock(cart.items)
          : (cart.items || []);
        var subtotal = items.reduce(function (s, it) {
          return s + (it.final_line_price != null ? it.final_line_price : (it.line_price || 0));
        }, 0);
        var count = items.reduce(function (s, it) { return s + (it.quantity || 0); }, 0);
        var body = {
          cart: {
            note: cart.note || null,
            zip: loc.pincode || '',
            lat: loc.latitude || '',
            long: loc.longitude || '',
            attributes: cart.attributes || {},
            original_total_price: subtotal,
            total_price: subtotal,
            total_discount: cart.total_discount,
            total_weight: cart.total_weight,
            item_count: count,
            items: items,
            requires_shipping: true,
            currency: cart.currency || 'INR',
            items_subtotal_price: subtotal,
            cart_level_discount_applications: [],
            checkout_charge_amount: subtotal
          }
        };
        return fetch(IPA_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (r) {
          return r.json();
        });
      })
      .then(function (data) {
        var resp = data && data.response;
        if (!resp) {
          container.hidden = true;
          return;
        }

        // Auto-remove any items the IPA backend flagged (cart-revamp.liquid:4043-4047):
        // e.g. a claimed gift that's no longer valid after the AOV dropped. Signature-
        // guarded so it acts once per cart state and converges (removed items drop out
        // of the next response). On removal, refresh the drawer instead of reloading.
        //
        // IMPORTANT: only remove gifts THIS module added — i.e. cart lines carrying the
        // freeGiftAOV property. Surprise gifts (added by sticky-cart-surprise-gift.js) are
        // managed there; removing them here would fight that module (it re-adds them) and
        // cause an infinite re-render loop (stuck gift/coupon loaders when only a gift remains).
        var ipaVariants = {};
        ((currentCart && currentCart.items) || []).forEach(function (it) {
          if (it.properties && it.properties.freeGiftAOV != null) ipaVariants[String(it.variant_id)] = true;
        });
        var removeItems = (resp.removeTheseFromCart || [])
          .map(function (el) { return el && (el.vId || el.variant_id); })
          .filter(Boolean)
          .filter(function (id) { return ipaVariants[String(id)]; });
        if (removeItems.length) {
          var sig = cartSig(currentCart);
          if (sig !== lastRemovedSig) {
            lastRemovedSig = sig;
            Promise.all(removeItems.map(function (id) { return removeVariant(id); }))
              .then(function () {
                if (typeof opts.onClaim === 'function') opts.onClaim(); // refresh + broadcast
              });
            return; // skip rendering the stale carousel; the refresh re-runs this module
          }
        }

        var gifts = (resp.freeGifts || []).filter(function (g) {
          return g.status !== 'Claimed';
        });
        if (!gifts.length) {
          container.hidden = true;
          return;
        }
        var unlocked = (resp.totalUnlockedGifts || 0) - (resp.totalClaimedGifts || 0);
        var total = (resp.totalGifts || 0) - (resp.totalClaimedGifts || 0);
        setText(container, '[data-st-gift-unlocked]', unlocked);
        setText(container, '[data-st-gift-total]', total);
        track.innerHTML = gifts.map(cardHtml).join('');
        wireClaims(track, opts);
        fireGiftAnalytics(track, gifts, total, unlocked);
        hideSkeleton();
        container.hidden = false;
      })
      .catch(function (err) {
        if (window.console) console.error('[StickyCart] gifts', err);
        container.hidden = true;
      });
  }

  // Pre-rendered "Sample" ribbon badges — purple while the tier is locked, green
  // once unlocked (the PNGs carry the star + "Sample" text).
  var SAMPLE_BADGE_LOCKED = 'https://cdn.shopify.com/s/files/1/0565/8021/0861/files/sample_lock.png?v=1781764213';
  var SAMPLE_BADGE_UNLOCKED = 'https://cdn.shopify.com/s/files/1/0565/8021/0861/files/sample_ok.png?v=1781764166';
  var LOCK_SVG =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M15.0705 8.2543H14.5806L14.5786 5.8131C14.5767 3.15377 12.4131 0.990206 9.75377 0.992189C7.09443 0.994172 4.93087 3.15773 4.93285 5.81707L4.93682 8.25826H4.76231C3.8481 8.26025 3.10642 9.00193 3.1084 9.91613V17.1842C3.1084 18.0984 3.85206 18.8401 4.76627 18.8401L15.0744 18.8341C15.9886 18.8341 16.7303 18.0925 16.7283 17.1782V9.9082C16.7283 8.99399 15.9847 8.2543 15.0705 8.2543ZM6.71368 8.25826L6.70971 5.81707C6.70971 4.13738 8.07408 2.77103 9.75377 2.76905C11.4335 2.76905 12.7998 4.1354 12.7998 5.8131L12.8038 8.2543L6.71368 8.25826Z" fill="#6C00B7" />' +
    '</svg>'

  var UNLOCK_SVG =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M15.2482 8.22607H4.59001C3.85151 8.22607 3.25317 8.82457 3.25317 9.56271V17.2553C3.25317 17.9934 3.85151 18.5921 4.59001 18.5921H15.2482C15.9865 18.5921 16.5848 17.9934 16.5848 17.2553V9.56271C16.5848 8.82459 15.9865 8.22607 15.2482 8.22607Z" fill="#2E7801" />' +
      '<path d="M7.49795 5.35557C7.4986 5.20536 7.50868 5.05594 7.52844 4.90704C7.51831 4.9825 7.50817 5.05793 7.49804 5.13338C7.53759 4.85379 7.6116 4.58177 7.71944 4.32079C7.69084 4.38858 7.66223 4.45637 7.63361 4.52418C7.7446 4.26181 7.88769 4.01587 8.06071 3.78956C8.01634 3.84699 7.97196 3.90444 7.92759 3.96187C8.10125 3.73846 8.30111 3.5386 8.52454 3.36492C8.46711 3.40929 8.40966 3.45366 8.35223 3.49804C8.57856 3.32502 8.82448 3.18193 9.08685 3.07094C9.01906 3.09956 8.95128 3.12817 8.88347 3.15677C9.14444 3.04892 9.41644 2.97491 9.69605 2.93536C9.6206 2.9455 9.54517 2.95564 9.46972 2.96577C9.76278 2.92687 10.058 2.9264 10.3513 2.96368C10.2759 2.95355 10.2004 2.94341 10.125 2.93328C10.4145 2.97305 10.6957 3.04943 10.9654 3.16198C10.8976 3.13338 10.8298 3.10477 10.762 3.07615C11.0244 3.18866 11.2699 3.33291 11.4961 3.50716C11.4386 3.46279 11.3812 3.41841 11.3237 3.37404C11.5385 3.5428 11.731 3.73534 11.8998 3.95009C11.8554 3.89266 11.811 3.83521 11.7667 3.77778C11.9409 4.00395 12.0852 4.2494 12.1977 4.51179C12.1691 4.444 12.1405 4.37622 12.1119 4.30841C12.2244 4.57816 12.3008 4.85929 12.3405 5.14884C12.3304 5.07339 12.3203 4.99794 12.3101 4.92249C12.3675 5.37353 12.3385 5.84265 12.3385 6.2961C12.3385 6.92811 12.3385 7.56011 12.3385 8.19211C12.3385 8.37022 12.3385 8.5483 12.3385 8.72641C12.3385 9.19052 12.7258 9.57781 13.1899 9.57781C13.654 9.57781 14.0413 9.19052 14.0413 8.72641C14.0413 7.71965 14.0413 6.71289 14.0413 5.70612C14.0413 5.58928 14.0413 5.47242 14.0413 5.35557C14.0413 4.67865 13.8674 4.02266 13.5599 3.4223C12.9981 2.3254 11.8605 1.49289 10.6429 1.30057C10.0201 1.20222 9.39743 1.21714 8.78728 1.38956C8.22325 1.54894 7.73458 1.83329 7.27882 2.19349C6.88641 2.50364 6.55631 2.9158 6.31428 3.35172C6.02444 3.87379 5.87549 4.42787 5.81242 5.01757C5.80042 5.12981 5.79566 5.24272 5.79517 5.35555C5.79319 5.81969 6.18396 6.20695 6.64657 6.20695C7.11217 6.20697 7.49597 5.81967 7.49795 5.35557Z" fill="#2E7801" />' +
    '</svg>'

  function priceMarkup(item) {
    var hasNumeric = !isNaN(parseInt(item.price, 10));
    if (item.trialfee == 1) {
      return '<span class="st-cart__gift-amount">₹' + (hasNumeric ? escapeHtml(item.price) : '0') + '</span>' +
        ' <span class="st-cart__gift-trial">Trial fee</span>';
    }
    if (!hasNumeric) {
      return '<span class="st-cart__gift-amount">Free</span>';
    }
    var html = '<span class="st-cart__gift-amount">₹' + escapeHtml(item.price) + '</span>';
    if (item.compartAtPrice) {
      html += ' <s class="st-cart__gift-mrp">₹' + parseInt(item.compartAtPrice, 10) + '</s>';
    }
    var off = parseInt(item.percentageDiscount, 10);
    if (!isNaN(off) && off > 0) {
      html += ' <span class="st-cart__gift-off">' + off + '% OFF</span>';
    }
    return html;
  }

  function cardHtml(item) {
    var locked = item.status === 'Locked';
    // The IPA-web/v2 items don't carry a `tags` field (the old cart merged it in via
    // Liquid), so key the ribbon off lock state — purple while locked, green once
    // unlocked — and show it on every tier (Figma 396-2876 / 425-24001).
    var tag = '<img class="st-cart__gift-tag" src="' + (locked ? SAMPLE_BADGE_LOCKED : SAMPLE_BADGE_UNLOCKED) +
      '" alt="' + escapeHtml(item.tags || 'Sample') + '" loading="lazy">';
    var progress = locked
      ? '<div class="st-cart__gift-progress"><span style="width:' + (item.loaderPercentage || 0) + '%"></span></div>'
      : '';

    // Stash the full IPA item on the card so a body tap can open the product drawer
    // (mirrors the existing cart passing the item JSON to selectItem()).
    var itemAttr = escapeHtml(JSON.stringify(item)).replace(/'/g, '&#39;');
    return (
      '<div class="st-cart__gift-card' + (locked ? ' is-locked' : '') + '"' +
      ' data-variant-id="' + escapeHtml(item.vId) + '"' +
      ' data-aov="' + (item.IPAData && item.IPAData.AOV != null ? item.IPAData.AOV : 0) + '"' +
      " data-st-gift-item='" + itemAttr + "'>" +
        '<div class="st-cart__gift-banner">' +
          '<span class="st-cart__gift-banner-icon">' + (locked ? LOCK_SVG : UNLOCK_SVG) + '</span>' +
          '<span class="st-cart__gift-banner-text">' + escapeHtml(item.headerTitle || '') + '</span>' +
        '</div>' +
        progress +
        '<div class="st-cart__gift-body" data-st-gift-open>' +
          '<div class="st-cart__gift-imgbox">' + tag +
            '<img class="st-cart__gift-img" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' +
          '</div>' +
          '<div class="st-cart__gift-details">' +
            '<p class="st-cart__gift-name">' + escapeHtml(item.title || '') + '</p>' +
            '<p class="st-cart__gift-size">' + escapeHtml(item.variantTitle || '') + '</p>' +
            '<div class="st-cart__gift-priceRow">' +
              '<div class="st-cart__gift-price">' + priceMarkup(item) + '</div>' +
              (locked
                ? '<button type="button" class="st-cart__gift-btn" disabled>Locked</button>'
                : '<button type="button" class="st-cart__gift-btn" data-st-gift-claim>Claim</button>') +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  // Build a Segment gift-tier payload from an IPA gift item.
  function giftPayload(item) {
    item = item || {};
    return {
      gift_product_id: item.pId || (item.IPAData && item.IPAData.productId) || null,
      gift_variant_id: item.vId || null,
      gift_product_name: item.title || null,
      gift_size: item.variantTitle || null,
      gift_trial_fee: item.sellingPrice != null ? item.sellingPrice : null,
      tier_status: (item.status || '').toLowerCase(),
      unlock_threshold: item.IPAData && item.IPAData.AOV != null ? item.IPAData.AOV : null
    };
  }

  // Carousel Viewed (once) + per-tier impressions + first-scroll.
  function fireGiftAnalytics(track, gifts, total, unlocked) {
    var A = window.StickyCartAnalytics;
    if (!A) return;
    if (typeof A.giftCarouselViewed === 'function') A.giftCarouselViewed(total, unlocked, null);
    var cards = track.querySelectorAll('.st-cart__gift-card');
    if (typeof A.observeImpression === 'function' && typeof A.giftTierViewed === 'function') {
      Array.prototype.forEach.call(cards, function (card, i) {
        var p = giftPayload(gifts[i] || {}); p.tier_index = i;
        A.observeImpression(card, function () { A.giftTierViewed(p); });
      });
    }
    if (typeof A.giftCarouselScrolled === 'function') {
      var fired = false;
      track.addEventListener('scroll', function () {
        if (fired) return; fired = true;
        A.giftCarouselScrolled(total, unlocked, 'right');
      }, { passive: true });
    }
  }

  function wireClaims(track, opts) {
    // Open the IPA product drawer ONLY when the IPA image or its name is tapped —
    // not the whole card body, and never the size, price row, or Claim button.
    var openers = track.querySelectorAll('[data-st-gift-open]');
    Array.prototype.forEach.call(openers, function (body) {
      body.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('[data-st-gift-claim]')) return; // claim handles itself
        if (!t.closest('.st-cart__gift-imgbox, .st-cart__gift-name')) return;
        var card = body.closest('.st-cart__gift-card');
        if (!card || !(window.StickyCartIpaDrawer && window.StickyCartIpaDrawer.open)) return;
        var item = null;
        try { item = JSON.parse(card.getAttribute('data-st-gift-item')); } catch (err) {}
        if (item) window.StickyCartIpaDrawer.open(item, { onClaim: opts.onClaim });
      });
    });

    var btns = track.querySelectorAll('[data-st-gift-claim]');
    Array.prototype.forEach.call(btns, function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.st-cart__gift-card');
        if (!card) return;
        var vId = card.getAttribute('data-variant-id');
        var aov = card.getAttribute('data-aov');
        var giftItem = null; try { giftItem = JSON.parse(card.getAttribute('data-st-gift-item')); } catch (e) {}
        var A = window.StickyCartAnalytics;
        if (btn.__claiming) return; // already claiming → ignore re-clicks during the delay
        btn.__claiming = true;
        // tier_index = the card's position in the carousel (matches Gift Tier Viewed). Computed
        // before removal so both Claim Clicked + Claimed carry it.
        var giftPayloadWithTier = giftPayload(giftItem);
        giftPayloadWithTier.tier_index = Array.prototype.indexOf.call(track.querySelectorAll('.st-cart__gift-card'), card);
        if (A && typeof A.giftClaimClicked === 'function') A.giftClaimClicked(giftPayloadWithTier);
        // Small delay, then animate the CTA from Claim → Claimed (CSS bg transition + pop) — no
        // "Adding…" step. Optimistic: reverted if the add fails.
        var claimTimer = setTimeout(function () { showClaimed(btn); }, 150);
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: vId, quantity: 1, properties: { freeGiftAOV: aov } }]
          })
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            // Limited stock (Figma 416-11641): Shopify returns 422 — revert the CTA, keep the card.
            if (data && data.status === 422) {
              clearTimeout(claimTimer);
              notifyLimitedStock();
              revertClaim(btn);
              btn.__claiming = false;
              return;
            }
            if (A && typeof A.giftClaimed === 'function') A.giftClaimed(giftPayloadWithTier);
            // Collapse the claimed card out so the remaining cards slide in to fill the gap,
            // then remove it and refresh (the carousel re-renders without the claimed item).
            removeCard(card, function () {
              if (typeof opts.onClaim === 'function') opts.onClaim();
            });
          })
          .catch(function (err) {
            clearTimeout(claimTimer);
            if (window.console) console.error('[StickyCart] claim', err);
            revertClaim(btn);
            btn.__claiming = false;
          });
      });
    });
  }

  var CLAIMED_CHECK_SVG =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M7.35138 0.552747C7.72514 0.234234 8.27486 0.234234 8.64862 0.552748L10.2028 1.87721C10.363 2.0137 10.5621 2.09618 10.7719 2.11292L12.8074 2.27536C13.2969 2.31442 13.6856 2.70313 13.7246 3.19264L13.8871 5.22814C13.9038 5.43791 13.9863 5.63704 14.1228 5.79721L15.4473 7.35138C15.7658 7.72514 15.7658 8.27486 15.4473 8.64862L14.1228 10.2028C13.9863 10.363 13.9038 10.5621 13.8871 10.7719L13.7246 12.8074C13.6856 13.2969 13.2969 13.6856 12.8074 13.7246L10.7719 13.8871C10.5621 13.9038 10.363 13.9863 10.2028 14.1228L8.64862 15.4473C8.27486 15.7658 7.72514 15.7658 7.35138 15.4473L5.79721 14.1228C5.63704 13.9863 5.43791 13.9038 5.22814 13.8871L3.19264 13.7246C2.70313 13.6856 2.31442 13.2969 2.27536 12.8074L2.11292 10.7719C2.09618 10.5621 2.0137 10.363 1.87721 10.2028L0.552747 8.64862C0.234234 8.27486 0.234234 7.72514 0.552748 7.35138L1.87721 5.79721C2.0137 5.63704 2.09618 5.43791 2.11292 5.22814L2.27536 3.19264C2.31442 2.70313 2.70313 2.31442 3.19264 2.27536L5.22814 2.11292C5.43791 2.09618 5.63704 2.0137 5.79721 1.87721L7.35138 0.552747Z" fill="#fff"/>' +
      '<path d="M4.5 9L6.1165 10.2125C6.21968 10.2899 6.34876 10.3244 6.47678 10.3088C6.6048 10.2933 6.72186 10.2288 6.8035 10.129L11 5" stroke="#28a51e" stroke-linecap="round"/>' +
    '</svg>';

  // Flip the CTA to the green "✓ Claimed" state.
  function showClaimed(btn) {
    if (!btn) return;
    btn.disabled = true;
    btn.classList.add('is-claimed');
    btn.innerHTML = CLAIMED_CHECK_SVG + '<span>Claimed</span>';
  }

  // Revert the CTA back to "Claim" (claim failed / limited stock).
  function revertClaim(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove('is-claimed');
    btn.textContent = 'Claim';
  }

  // Collapse the claimed card out (width → 0) so the rest of the carousel slides in to fill the
  // gap, then remove it from the DOM and run the refresh callback (which re-renders the carousel).
  function removeCard(card, done) {
    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      if (card && card.parentNode) card.parentNode.removeChild(card);
      if (typeof done === 'function') done();
    }
    if (!card) { finish(); return; }
    // Brief hold so the "Claimed" state is visible, then animate the collapse.
    setTimeout(function () {
      // Lock the current height first: as width → 0 the content (e.g. the 2-line product name)
      // would otherwise reflow into more lines and stretch the card — and the whole row — taller.
      card.style.height = card.offsetHeight + 'px';
      card.addEventListener('transitionend', function te(e) {
        if (e.target !== card || (e.propertyName !== 'width' && e.propertyName !== 'flex-basis')) return;
        card.removeEventListener('transitionend', te);
        finish();
      });
      card.classList.add('is-removing');
      setTimeout(finish, 700); // fallback if transitionend doesn't fire
    }, 450);
  }

  function notifyLimitedStock() {
    var msg = 'Sorry, we have limited quantity available for this item!';
    if (window.StickyCart && typeof window.StickyCart.toast === 'function') {
      window.StickyCart.toast(msg, { error: true });
    } else if (window.alert) {
      window.alert(msg);
    }
  }

  function setText(scope, sel, val) {
    var el = scope.querySelector(sel);
    if (el) el.textContent = val;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  window.StickyCartGifts = { apply: apply };
})();
